import type { ComponentCheck } from './hooks/useTierEvaluation';
import type { PreviousTierInfo } from './hooks/useTierEvaluation';
import { TierCard } from './components/TierCard';
import {
  getTierIdentityColor,
  getTierBorderGradient,
} from './utils/tierGradients';
import { isTierZero } from './tier0/tierZero';
import { AboutTiersTier0 } from './tier0/AboutTiersTier0';
import type { TierLadderEntry } from '../../../services/rewards/RewardsService';

interface TierDisplayInfo {
  tierId: string;
  name: string;
  earnRateDescription?: string;
  maxEarningsDescription?: string;
  componentChecks?: ComponentCheck[];
}

interface NextTierDisplayInfo extends TierDisplayInfo {
  componentChecks: ComponentCheck[];
}

interface AboutTiersProps {
  tier: TierDisplayInfo | null;
  nextTier: NextTierDisplayInfo | null;
  previousTier: PreviousTierInfo | null;
  /** Live tier collection for the ladder rungs above the next tier. */
  ladder?: TierLadderEntry[];
  loading: boolean;
  onBack: () => void;
  onLearnMore: () => void;
}

export const AboutTiers = ({
  tier,
  nextTier,
  previousTier,
  ladder = [],
  loading,
  onBack,
  onLearnMore,
}: AboutTiersProps) => {
  if (loading) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0b0618' }}>
        <p style={{ color: '#8d8aa8', fontSize: 14 }}>Loading tier info...</p>
      </div>
    );
  }

  if (tier && isTierZero(tier.tierId)) {
    return (
      <AboutTiersTier0
        tier={tier}
        nextTier={nextTier}
        ladder={ladder}
        onBack={onBack}
        onLearnMore={onLearnMore}
      />
    );
  }

  const currentTierId = tier?.tierId ?? 'tier-1';
  const currentTierName = tier?.name ?? 'Tier 1';
  const isMaxTier = !nextTier;
  const titleColor = getTierIdentityColor(currentTierId);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
        padding: '0 0 14px',
        background: '#0b0618',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: 22,
          marginBottom: 12,
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={onBack}
          style={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            height: 22,
          }}
        >
          <span style={{ margin: 0, color: '#8d8aa8', fontSize: 12, lineHeight: '22px', fontWeight: 700 }}>
            ← Back
          </span>
        </button>
      </div>

      {/* Scrollable content */}
      <div
        className="hr-scroll"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', alignItems: 'center' }}>
          {/* Title + Large tier name */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%', alignItems: 'center' }}>
            <p style={{ margin: 0, color: '#cccccc', fontSize: 14, lineHeight: '20px', fontWeight: 700, textAlign: 'center' }}>
              Machine Tier
            </p>
            <p style={{ margin: 0, color: titleColor, fontSize: 42, lineHeight: 1, fontWeight: 700, textAlign: 'center' }}>
              {currentTierName.toUpperCase()}
            </p>
          </div>
          {isMaxTier ? (
            <MaxTierContent
              tier={tier}
              previousTier={previousTier}
              onLearnMore={onLearnMore}
            />
          ) : (
            <EarningRatesContent
              tier={tier}
              nextTier={nextTier}
              onLearnMore={onLearnMore}
            />
          )}
        </div>
      </div>

      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          paddingTop: 8,
          paddingBottom: 4,
        }}
      >
        <a
          href="https://discord.gg/playcast"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: '#964fff',
            textDecoration: 'underline rgba(150,79,255,0.5)',
            textUnderlineOffset: 2,
            fontSize: 10,
          }}
        >
          Get help · Discord
        </a>
      </div>
    </div>
  );
};

function EarningRatesContent({
  tier,
  nextTier,
  onLearnMore,
}: {
  tier: TierDisplayInfo | null;
  nextTier: NextTierDisplayInfo | null;
  onLearnMore: () => void;
}) {
  const currentTierId = tier?.tierId ?? 'tier-1';

  return (
    <>
      {/* Section label */}
      <p style={{ margin: 0, color: '#8d8aa8', fontSize: 10, lineHeight: '16px', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', alignSelf: 'flex-start' }}>
        EARNING RATES
      </p>

      {/* Current Tier Card */}
      {tier && (
        <TierCard
          name={tier.name}
          earnRateDescription={tier.earnRateDescription}
          maxEarningsDescription={tier.maxEarningsDescription}
          variant="current"
          borderGradient={getTierBorderGradient(currentTierId)}
        />
      )}

      {/* Next Tier Card (locked) */}
      {nextTier && (
        <TierCard
          name={nextTier.name}
          earnRateDescription={nextTier.earnRateDescription}
          maxEarningsDescription={nextTier.maxEarningsDescription}
          variant="next"
          onLearnMore={onLearnMore}
        />
      )}
    </>
  );
}

function MaxTierContent({
  tier,
  previousTier,
  onLearnMore,
}: {
  tier: TierDisplayInfo | null;
  previousTier: PreviousTierInfo | null;
  onLearnMore: () => void;
}) {
  const currentTierId = tier?.tierId ?? 'tier-2';

  return (
    <>
      {/* Section label */}
      <p style={{ margin: 0, color: '#8d8aa8', fontSize: 10, lineHeight: '16px', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', alignSelf: 'flex-start' }}>
        EARNING RATES
      </p>

      {/* Current Tier Card */}
      {tier && (
        <TierCard
          name={tier.name}
          earnRateDescription={tier.earnRateDescription}
          maxEarningsDescription={tier.maxEarningsDescription}
          variant="current"
          borderGradient={getTierBorderGradient(currentTierId)}
        />
      )}

      {/* Previous Tier Card (locked, lower tier) */}
      {previousTier && (
        <TierCard
          name={previousTier.name}
          earnRateDescription={previousTier.earnRateDescription}
          maxEarningsDescription={previousTier.maxEarningsDescription}
          variant="previous"
        />
      )}

      {/* Learn More button */}
      <button
        type="button"
        onClick={onLearnMore}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          minHeight: 44,
          padding: '12px 24px',
          borderRadius: 9999,
          border: 'none',
          background: '#3f3564',
          color: '#ffffff',
          fontSize: 15,
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        Learn More
      </button>
    </>
  );
}
